/**
 * Host component for resolving a single pending decision inline in the
 * field-scoped Inspector (replaces the standalone PendingDecisions
 * drawer added by #587).
 *
 * Switches on `decision.kind` to render the appropriate sub-component
 * from `decisions/components.tsx`. Action buttons are gated by
 * `ALLOWED_ACTIONS_BY_KIND` (server-driven). `dismiss` requires a
 * rationale; `defer` accepts an optional note. Disabled when a
 * resolve mutation is in-flight or when the recipe is in a
 * non-editable lifecycle state (exporting / failed / etc.).
 */
import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  ALLOWED_ACTIONS_BY_KIND,
  type PendingDecision,
  type ResolveAction,
} from '../../services/mappingApi';
import {
  KindSpecificActions,
  KIND_LABELS,
  SeverityIcon,
  UniversalActions,
} from './decisions/components';
import { ConsideredSourcesAccordion } from './decisions/ConsideredSourcesAccordion';
import { CorrectionHistoryAccordion } from './decisions/CorrectionHistoryAccordion';

export interface FieldDecisionActionsProps {
  decision: PendingDecision;
  disabled: boolean;
  onResolve: (action: ResolveAction) => void;
  /**
   * Set of rule names already overridden on this field. When the
   * decision targets a rule already in the set, `accept_override`
   * is suppressed so the operator can't double-override.
   */
  overriddenRuleNames?: Set<string>;
  /**
   * Plan §G/5: target field is missing from current schema snapshot
   * (e.g. column was dropped after the decision was raised). When
   * true, only `dismiss` and `defer` are surfaced.
   */
  orphanTarget?: boolean;
}

export const FieldDecisionActions: React.FC<FieldDecisionActionsProps> = ({
  decision,
  disabled,
  onResolve,
  overriddenRuleNames,
  orphanTarget,
}) => {
  const description = decision.payload.description ?? decision.message;
  const rules = decision.payload.validator_rules ?? [];
  const kindLabel = KIND_LABELS[decision.kind] ?? { label: decision.kind, help: '' };
  const allowed = ALLOWED_ACTIONS_BY_KIND[decision.kind] ?? [];

  return (
    <Box
      sx={{
        p: 1.25,
        my: 0.75,
        border: '1px solid rgba(0,0,0,0.08)',
        borderLeft: (theme) =>
          `3px solid ${
            decision.severity === 'error'
              ? theme.palette.error.main
              : theme.palette.warning.main
          }`,
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <SeverityIcon severity={decision.severity} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {decision.target_table}.{decision.target_field}
        </Typography>
        <Tooltip title={kindLabel.help}>
          <Chip label={kindLabel.label} size="small" color="info" />
        </Tooltip>
        <Chip label={decision.code} size="small" variant="outlined" />
      </Stack>

      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {description}
        </Typography>
      ) : null}

      {rules.length > 0 ? (
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
          {rules.map((r, idx) => (
            <Tooltip key={idx} title={r.message ?? ''}>
              <Chip
                label={r.rule ?? r.type ?? 'rule'}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            </Tooltip>
          ))}
        </Stack>
      ) : null}

      {orphanTarget ? (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1, mb: 0.5 }}>
          Orphan target — only dismiss / defer are available.
        </Typography>
      ) : (
        <KindSpecificActions
          decision={decision}
          disabled={disabled}
          onResolve={onResolve}
          overriddenRuleNames={overriddenRuleNames}
        />
      )}
      <UniversalActions
        allowed={
          orphanTarget
            ? (['dismiss', 'defer'] as ResolveAction['action'][]).filter((a) =>
                allowed.includes(a as any),
              )
            : allowed
        }
        disabled={disabled}
        onResolve={onResolve}
      />

      {decision.payload.considered_sources && decision.payload.considered_sources.length > 0 ? (
        <ConsideredSourcesAccordion considered={decision.payload.considered_sources} />
      ) : null}

      {decision.correction_history && decision.correction_history.length > 0 ? (
        <CorrectionHistoryAccordion attempts={decision.correction_history} />
      ) : null}

      {decision.deferred_count && decision.deferred_count > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Deferred {decision.deferred_count}× — last on{' '}
          {decision.last_deferred_at?.slice(0, 10) ?? 'unknown'}
          {decision.last_deferred_note ? `: ${decision.last_deferred_note}` : ''}
        </Typography>
      ) : null}
    </Box>
  );
};

export default FieldDecisionActions;
